/**
 * Get decimal places for a price based on symbol.
 * Forex: 5 digits (JPY pairs: 3), Metals/Crypto/Indices: 2
 */
export function priceDecimals(symbol: string): number {
  const s = symbol.toUpperCase().replace(/[+._\-].*$/, '');
  if (s.includes('JPY')) return 3;
  if (['XAUUSD', 'XAGUSD', 'BTCUSD', 'ETHUSD', 'US30', 'NAS100', 'SPX500'].some(m => s.includes(m))) return 2;
  // Forex pairs default to 5 digits
  if (s.length === 6 && /^[A-Z]{6}$/.test(s)) return 5;
  return 2;
}

/** Format a price with appropriate decimal places for the symbol */
export function formatPrice(price: number | string | null | undefined, symbol: string): string {
  const num = typeof price === 'number' ? price : parseFloat(String(price));
  if (isNaN(num)) return '—';
  return num.toFixed(priceDecimals(symbol));
}

/**
 * Get contract size for P&L calculation based on symbol.
 *
 * For JPY-quoted pairs, pass livePrices to look up USDJPY for conversion.
 * For other cross pairs, pass livePrices for quote currency conversion.
 */
export function getClientContractSize(
  symbol: string,
  livePrices?: Record<string, { ask: number; bid: number }>,
): number {
  const s = symbol.toUpperCase().replace(/[+._\-].*$/, '');

  // Metals
  if (s === 'XAUUSD') return 100;
  if (s === 'XAGUSD') return 5000;

  // Crypto
  if (['BTCUSD', 'ETHUSD'].some(c => s.includes(c))) return 1;

  // Indices
  if (['US30', 'NAS100', 'SPX500', 'UK100', 'DE40', 'JP225'].some(i => s.includes(i))) return 1;

  // Forex
  if (s.length === 6 && /^[A-Z]{6}$/.test(s)) {
    const quote = s.slice(3);
    if (quote === 'USD') return 100000; // P&L already in USD

    // JPY-quoted: need USDJPY rate to convert JPY P&L to USD
    if (quote === 'JPY') {
      const usdjpy = livePrices?.['USDJPY+'] || livePrices?.['USDJPY'];
      let rate = 150; // fallback
      if (usdjpy) {
        const bid = typeof usdjpy.bid === 'string' ? parseFloat(usdjpy.bid) : usdjpy.bid;
        const ask = typeof usdjpy.ask === 'string' ? parseFloat(usdjpy.ask) : usdjpy.ask;
        if (!isNaN(bid) && !isNaN(ask) && bid > 0) rate = (bid + ask) / 2;
      }
      return 100000 / rate;
    }

    // Other cross pairs (EURGBP, AUDNZD, etc.) — approximate as USD
    return 100000;
  }

  return 100;
}

/**
 * Format a number as currency using Intl.NumberFormat.
 */
export function formatCurrency(value: number, currency = 'USD', opts?: { compact?: boolean }): string {
  if (opts?.compact && Math.abs(value) >= 1000) {
    const sign = value < 0 ? '-' : '';
    const abs = Math.abs(value);
    const sym = getCurrencySymbol(currency);
    if (abs >= 1_000_000) return `${sign}${sym}${(abs / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `${sign}${sym}${(abs / 1_000).toFixed(1)}K`;
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Format a number as USD currency (backward-compatible alias).
 */
export function formatUsd(value: number, opts?: { compact?: boolean }): string {
  return formatCurrency(value, 'USD', opts);
}

/**
 * Get compact currency symbol for a currency code.
 */
export function getCurrencySymbol(currency: string): string {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency, currencyDisplay: 'narrowSymbol' })
      .formatToParts(0)
      .find(p => p.type === 'currency')?.value ?? currency;
  } catch {
    return currency;
  }
}

/**
 * Extract the currency from a bot's mt5Account, defaulting to USD.
 */
export function botCurrency(bot: { mt5Account?: { currency?: string } | null }): string {
  return bot.mt5Account?.currency || 'USD';
}

/**
 * Format a number with sign prefix: +$1,234.56 / -$1,234.56
 */
export function formatPnl(value: number, currency = 'USD'): string {
  const formatted = formatCurrency(Math.abs(value), currency);
  if (value > 0) return `+${formatted}`;
  if (value < 0) return `-${formatted}`;
  return formatted;
}

/**
 * Format a percentage: +12.5% / -3.2%
 */
export function formatPct(value: number): string {
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
}

/**
 * Format a number with commas
 */
export function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

/**
 * Get PnL color class
 */
export function pnlColor(value: number): string {
  if (value > 0) return 'text-green';
  if (value < 0) return 'text-red';
  return 'text-ink-3';
}

/**
 * Format relative time (e.g., "2m ago", "3h ago")
 */
export function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = now - then;

  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return `${seconds}s ago`;

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/**
 * Convert raw API error messages into user-friendly text.
 */
export function friendlyError(raw: string | null | undefined, fallback: string): string {
  const msg = raw ?? '';
  if (msg.includes('429') || /too many|rate.limit/i.test(msg)) {
    return 'Too many requests — please wait a moment and try again.';
  }
  if (msg.includes('timeout') || msg.includes('ETIMEDOUT') || msg.includes('ECONNABORTED')) {
    return 'Request timed out — please try again.';
  }
  if (msg.includes('Network Error') || msg.includes('ECONNREFUSED')) {
    return 'Unable to reach the server — check your connection.';
  }
  if (/stalled/i.test(msg)) {
    return 'Processing took longer than expected and was interrupted. Please try again.';
  }
  if (msg.includes('ENOMEM') || msg.includes('heap out of memory')) {
    return 'The server ran out of memory processing your request. Please try again.';
  }
  return msg || fallback;
}
